import {
    CurrencyISOCode,
    FormattedPriceCalculationResponse,
    PricingStrategy,
    PricingTier
} from "../constants";

import {
    getFormattedPrice,
    getFormattedUnitAmount,
    getTwoDigitFormattedPrice
} from "./amounts-format";
import calculateFixedCost from "./calculations/fixed-cost";
import {
    default as calculateMarginalPerUnit,
    default as marginalPerUnit
} from "./calculations/marginal-per-unit";

const getTierIdxForNumUnits = (numUnits: number, tiers: PricingTier[]) =>
    tiers.findIndex(
        ({ unitLimitLower, unitLimitUpper }) =>
            numUnits >= (unitLimitLower || 0) &&
            numUnits <= (unitLimitUpper || Math.pow(10, 1000))
    );

export function calculatePrice(
    numUnits: number,
    tiers: PricingTier[],
    currencyISOCode: CurrencyISOCode,
    localeISOCode = "en-US"
): FormattedPriceCalculationResponse {
    if (!Number.isInteger(numUnits) || numUnits < 1) {
        throw new Error(
            `Number of units must be a positive non-zero integer, got ${numUnits}.`
        );
    }

    if (!tiers || tiers.length === 0) {
        throw new Error(
            `Price tiers must not be null, undefined or an empty array, got ${tiers}.`
        );
    }

    tiers.sort((a: PricingTier, b: PricingTier) => {
        return (a.unitLimitLower ?? 0) - (b.unitLimitLower ?? 0);
    });

    let matchingTierIdx = getTierIdxForNumUnits(numUnits, tiers);

    const matchingTier = tiers[matchingTierIdx];
    const finalTier = tiers[tiers.length - 1];
    let adjustedNumberOfUnits: number = numUnits;
    let hasExceededMaxTier = false;

    let calcFunc;

    if (matchingTier !== undefined) {
        const tierPricingStrategy = matchingTier.pricingStrategy;
        calcFunc =
            tierPricingStrategy === PricingStrategy.MARGINAL_PER_UNIT
                ? calculateMarginalPerUnit
                : calculateFixedCost;
    } else if (
        finalTier &&
        finalTier.pricingStrategy === PricingStrategy.MARGINAL_PER_UNIT
    ) {
        calcFunc = marginalPerUnit;
    } else {
        /*
         * When a user input is greater than the max tier limit
         * we will reset the number of users to max tier limit
         * of that product and perform price calculations.
         * */
        adjustedNumberOfUnits = finalTier?.unitLimitUpper || 0;
        matchingTierIdx = getTierIdxForNumUnits(adjustedNumberOfUnits, tiers);
        calcFunc = calculateFixedCost;
        hasExceededMaxTier = true;
    }

    const fmtUnit = (amount: number | null) =>
        getFormattedUnitAmount(localeISOCode, amount);

    const fmtPrice = (price: number) =>
        getFormattedPrice(currencyISOCode, price);

    const { totalPrice, avgPerUnitPrice, hasFlatRateFirstTier, breakdown } =
        calcFunc(
            adjustedNumberOfUnits,
            matchingTierIdx,
            tiers,
            currencyISOCode
        );

    return {
        totalPrice,
        formattedTotalPrice: fmtPrice(totalPrice),

        avgPerUnitPrice,
        formattedAvgPerUnitPrice: fmtPrice(avgPerUnitPrice),

        breakdown,

        hasFlatRateFirstTier,
        formattedBreakdown: breakdown.map(
            ({
                unitsInTier,
                unitLimitLower,
                unitLimitUpper,
                unitsAvailableInTier,
                totalPriceForTier,
                avgPerUnitPriceForTier
            }) => ({
                formattedUnitsInTier: fmtUnit(unitsInTier),
                formattedUnitsAvailableInTier: fmtUnit(unitsAvailableInTier),

                formattedUnitLimitLower: fmtUnit(unitLimitLower),
                formattedUnitLimitUpper: fmtUnit(unitLimitUpper),

                formattedTotalPriceForTier: getTwoDigitFormattedPrice(
                    currencyISOCode,
                    totalPriceForTier
                ),
                formattedAvgPerUnitPriceForTier: getTwoDigitFormattedPrice(
                    currencyISOCode,
                    avgPerUnitPriceForTier
                )
            })
        )
    };
}
